import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./CustomUploadFile.module.css";
import pdfIcon from "./../../../assets/icons/export-pdf.png"; // Adjust this path as necessary
import { retrieveBundleFiles, deleteFileFromBundle } from "../../../services/C4S Decommission - Replacement/editVehicleWTA"; // FIX: corrected relative path (removed extraneous /src/...)

// ======================================================
// Helper Modal Component (can be in its own file too)
// ======================================================
const Modal = ({ show, onClose, children }) => {
  if (!show) {
    return null;
  }

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <span className={styles.modalCloseButton} onClick={onClose}>
          &times;
        </span>
        {children}
      </div>
    </div>
  );
};

Modal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};

// ======================================================
// Main VehicleImageUpload Component
// ======================================================
const VehicleImageUpload = ({ onImagesChange, vehicleId, bundleId, setIsLoading, showCustomAlert, RETRIEVE_BASE_URL }) => {
  // State to store the currently selected/uploaded files (images or PDFs)
  const [currentImages, setCurrentImages] = useState([]);
  const [fetchError, setFetchError] = useState(null); // NEW: track fetch/network/validation errors
  const [enableAsset, setEnableAsset] = useState(false);
  const [localLoading, setLocalLoading] = useState(false); // Local loading state

  // Ref to directly access the hidden file input element
  const fileInputRef = useRef(null);

  // State for managing the modal
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null); // Will hold the JSX for image preview

  // Configuration constants
  const MAX_IMAGES = 3; // Maximum number of files allowed
  const MAX_FILE_SIZE_MB = 10; // Maximum file size in MB
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // Convert MB to bytes

  // ======================================================
  // API Integration: Fetch Existing Files on Mount
  // ======================================================
  useEffect(() => {
    // Do not fetch anything until user enables images
    if (!enableAsset) {
      setCurrentImages([]); // clear any previously loaded images when disabled
      setFetchError(null);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchExistingFiles = async () => {
      setFetchError(null);

      // Validate IDs before attempting network call
      const validVehicle = typeof vehicleId === "string" && vehicleId.trim() !== "";
      const validBundle = typeof bundleId === "string" && bundleId.trim() !== "";
      if (!validVehicle || !validBundle) {
        if (isMounted) {
          setLocalLoading(false);
          setFetchError("missing-identifiers");
          showCustomAlert?.("Missing vehicle or bundle identifier. Cannot load images.", "error");
        }
        return;
      }

      if (isMounted) {
        setLocalLoading(true); // Use local loading instead of global
      }

      try {
        const response = await retrieveBundleFiles(vehicleId, bundleId, { signal: controller.signal });

        if (!isMounted) return;

        if (!response || typeof response.status !== "number") {
          setLocalLoading(false);
          setFetchError("network");
          showCustomAlert?.("No response from server while retrieving files.", "error");
          return;
        }

        if (response.status >= 200 && response.status < 300) {
          const fetchedFilesData = response.data?.data;

          if (!Array.isArray(fetchedFilesData)) {
            setLocalLoading(false);
            setFetchError("invalid-data");
            showCustomAlert?.("Invalid data format received from API. Expected an array of URLs.", "error");
            return;
          }

          if (!fetchedFilesData.length) {
            setCurrentImages([]);
            setLocalLoading(false);
            showCustomAlert?.("No existing files found for this vehicle.", "info");
            return;
          }

          const mapped = fetchedFilesData
            .map((fileUrl) => {
              if (typeof fileUrl !== "string") return null;
              const urlParts = fileUrl.split("/");
              const fileNameWithParams = urlParts[urlParts.length - 1];
              const fileName = fileNameWithParams.split("?")[0];
              const lower = fileName.toLowerCase();
              const isPdf = lower.endsWith(".pdf");
              const isImg = lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg");

              let fileType = "unknown";
              if (isPdf) fileType = "application/pdf";
              else if (isImg) fileType = "image/"; // generic image marker

              let fileSrc = "";
              if (isPdf) {
                fileSrc = pdfIcon || "";
                if (!pdfIcon) showCustomAlert?.("PDF icon missing.", "error");
              } else if (isImg) {
                fileSrc = fileUrl; // direct URL for image
              } else {
                fileSrc = "https://placehold.co/150x150/cccccc/333333?text=Unknown";
              }

              return {
                id: fileUrl,
                name: decodeURIComponent(fileName),
                src: fileSrc,
                file: null, // existing files not re-uploaded yet
                type: fileType,
                isExisting: true,
                originalUrl: fileUrl, // store original for both images & pdfs (needed for PDF download)
              };
            })
            .filter(Boolean);

          setCurrentImages(mapped);
          setLocalLoading(false);
          showCustomAlert?.(`Successfully loaded ${mapped.length} existing files.`, "success");
        } else {
          const errorMessage = response.data?.message || `HTTP ${response.status}`;
          setLocalLoading(false);
          setFetchError("http-error");
          showCustomAlert?.(`Failed to retrieve existing files: ${errorMessage}`, "error");
        }
      } catch (error) {
        if (!isMounted) return;

        setLocalLoading(false);

        if (controller.signal.aborted) {
          setFetchError("cancelled");
          showCustomAlert?.("File loading was cancelled.", "info");
          return;
        }

        if (error?.response) {
          setFetchError("http-error");
          const errorMsg = error.response.data?.message || error.response.statusText || "Unknown server error";
          showCustomAlert?.(`Server error ${error.response.status}: ${errorMsg}`, "error");
        } else if (error?.request) {
          setFetchError("network");
          showCustomAlert?.("Network error retrieving files. Please check your connection and try again.", "error");
        } else {
          setFetchError("unexpected");
          showCustomAlert?.(`Unexpected error loading files: ${error.message || "Unknown error"}`, "error");
        }
      }
    };

    fetchExistingFiles();

    return () => {
      isMounted = false;
      controller.abort();
      currentImages.forEach((file) => {
        if (file.src && file.src.startsWith("blob:")) URL.revokeObjectURL(file.src);
      });
    };
  }, [vehicleId, bundleId, enableAsset]); // REMOVED pdfIcon to avoid unnecessary refetches

  /**
   * Handles file selection, either from a browse dialog or a drag-and-drop event.
   * @param {Event} event The change event from the file input or the drop event.
   */
  const handleFileChange = (event) => {
    if (!enableAsset) return; // guard
    // Get files from the input event or the dataTransfer (for drag-and-drop)
    const files = Array.from(event.target.files || event.dataTransfer.files);
    processFiles(files); // Call a helper to process the files
    // Clear the input value to allow selecting the same file(s) again if desired
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /**
   * Processes an array of File objects, performing validation and generating previews.
   * Updates component state and notifies parent.
   * @param {Array<File>} files An array of File objects to process.
   */
  const processFiles = (files) => {
    if (!enableAsset) return; // guard
    // First, identify files that are of a valid type and size.
    const filesToProcess = files.filter((file) => {
      const isImage = file.type.startsWith("image/png") || file.type.startsWith("image/jpeg");
      const isPdf = file.type === "application/pdf";
      return (isImage || isPdf) && file.size <= MAX_FILE_SIZE_BYTES;
    });

    // Check if adding these files would exceed the maximum limit.
    if (currentImages.length + filesToProcess.length > MAX_IMAGES) {
      return; // Stop processing if the limit would be exceeded
    }

    // Process each valid file
    filesToProcess.forEach((file) => {
      const isImage = file.type.startsWith("image/png") || file.type.startsWith("image/jpeg");
      const isPdf = file.type === "application/pdf";

      // Prepare the data object for the new file entry
      const newFileEntry = {
        id: Date.now() + Math.random(), // Generate a unique ID
        name: file.name,
        src: "", // This will be filled with Data URL for images or icon path for PDFs
        file: file, // Store the original File object
        type: file.type, // Store file type for conditional rendering
      };

      if (isImage) {
        // For images, use FileReader to get a Data URL for preview
        const reader = new FileReader();
        reader.onloadend = () => {
          newFileEntry.src = reader.result; // Set Data URL
          // Update state and notify parent immediately after image is read
          setCurrentImages((prevImages) => {
            const updated = [...prevImages, newFileEntry];
            onImagesChange(updated.map((f) => f.file));
            return updated;
          });
        };
        reader.readAsDataURL(file); // Start reading the image file
      } else if (isPdf) {
        // For PDFs, use the static icon path directly
        newFileEntry.src = pdfIcon; // Use the imported pdfIcon
        // Update state and notify parent immediately for PDF (no async read needed)
        setCurrentImages((prevImages) => {
          const updated = [...prevImages, newFileEntry];
          onImagesChange(updated.map((f) => f.file));
          return updated;
        });
      }
    });

    // Alert for files that were initially passed but failed validation (type/size)
    files.forEach((file) => {
      const isImage = file.type.startsWith("image/png") || file.type.startsWith("image/jpeg");
      const isPdf = file.type === "application/pdf";
    });
  }; // End of processFiles function

  /**
   * Handles the 'dragover' event for the upload area.
   * Prevents default browser behavior and adds a visual cue for dropping.
   * @param {Event} event
   */
  const handleDragOver = (event) => {
    event.preventDefault(); // Crucial to allow a drop
    event.stopPropagation();
    event.currentTarget.classList.add(styles.dragOver); // Add CSS class for visual feedback
  };

  /**
   * Handles the 'dragleave' event for the upload area.
   * Removes the visual cue.
   * @param {Event} event
   */
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.remove(styles.dragOver); // Remove CSS class
  };

  /**
   * Handles the 'drop' event for the upload area.
   * Prevents default browser behavior and processes the dropped files.
   * @param {Event} event
   */
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.remove(styles.dragOver); // Remove CSS class
    handleFileChange(event); // Reuse the file handling logic
  };

  /**
   * Handles the deletion of a file from the display.
   * @param {number} idToRemove The unique ID of the file to remove.
   */
  const handleDeleteImage = async (idToRemove) => {
    if (!enableAsset) return; // guard
    const fileToDelete = currentImages.find((fileData) => fileData.id === idToRemove);

    if (!fileToDelete) {
      return;
    }

    if (fileToDelete.isExisting) {
      // This file exists on the backend, call the delete API
      if (!vehicleId || !bundleId || !fileToDelete.name) {
        if (showCustomAlert) {
          showCustomAlert("Cannot delete existing file: Missing vehicle ID, bundle ID, or file name.", "error");
        }
        return;
      }

      setLocalLoading(true); // Use local loading for delete operations
      try {
        const response = await deleteFileFromBundle(vehicleId, bundleId, fileToDelete.name);

        // Assuming deleteFileFromBundle returns a response object with a status
        if (response.status >= 200 && response.status < 300) {
          const updatedImages = currentImages.filter((fileData) => fileData.id !== idToRemove);
          setCurrentImages(updatedImages);
          // No need to call onImagesChange for API-deleted files,
          // as they are already on the backend and removed.
          if (showCustomAlert) {
            showCustomAlert(`File '${fileToDelete.name}' deleted successfully.`, "success");
          }
        } else {
          if (showCustomAlert) {
            showCustomAlert(`Failed to delete file '${fileToDelete.name}': ${response.data?.message || response.data || "Unknown error"}`, "error");
          }
        }
      } catch (error) {
        if (showCustomAlert) {
          showCustomAlert(`Error deleting file '${fileToDelete.name}'. Please try again.`, "error");
        }
      } finally {
        setLocalLoading(false);
      }
    } else {
      // This is a newly added file not yet uploaded, just remove it from state
      const updatedImages = currentImages.filter((fileData) => fileData.id !== idToRemove);
      setCurrentImages(updatedImages);
      // Notify parent about the change in newly selected files.
      // Filter out existing files (which have file: null)
      onImagesChange(updatedImages.map((fileData) => fileData.file).filter(Boolean));
      if (showCustomAlert) {
        showCustomAlert(`File '${fileToDelete.name}' removed from selection.`, "info");
      }
    }
  };

  /**
   * Handles the click on a preview thumbnail (image or PDF).
   * Opens image in modal or triggers PDF download.
   * @param {object} fileData The file data object from currentImages state.
   */
  const handlePreviewClick = (fileData) => {
    if (fileData.type.startsWith("image/")) {
      // For images, set modal content to the image and show modal
      setModalContent(<img src={fileData.src} alt={fileData.name} className={styles.modalImage} />);
      setShowModal(true);
    } else if (fileData.type === "application/pdf") {
      // Use originalUrl for existing PDFs; if a newly added PDF (file exists) create object URL
      if (fileData.originalUrl && fileData.isExisting) {
        window.open(fileData.originalUrl, "_blank", "noopener,noreferrer");
      } else if (fileData.file) {
        const url = URL.createObjectURL(fileData.file);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileData.name); // Suggest the original filename for download
        document.body.appendChild(link); // Append to body to make it clickable
        link.click(); // Programmatically click the link
        document.body.removeChild(link); // Clean up the link element
        URL.revokeObjectURL(url); // Release the object URL from memory
      }
    }
  };

  // Toggle: if true, prevent new uploads on network failure
  const blockOnNetworkFailure = true;
  // Determine if the upload area/button should be disabled (when max files are reached)
  const isUploadDisabled = currentImages.length >= MAX_IMAGES || (blockOnNetworkFailure && fetchError === "network");

  const handleEnableToggle = async () => {
    try {
      if (!enableAsset) {
        // Enabling - show immediate feedback but don't block
        showCustomAlert?.("Enabling image upload feature...", "info");
        setEnableAsset(true);
      } else {
        // Disabling - immediate action
        setEnableAsset(false);
        setCurrentImages([]);
        setFetchError(null);
        setLocalLoading(false);
        showCustomAlert?.("Image upload feature disabled.", "info");
      }
    } catch (error) {
      showCustomAlert?.(`Error toggling image feature: ${error.message}`, "error");
    }
  };

  return (
    <div className={styles.container}>
      {/* Enable / Disable toggle */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 className={styles.header} style={{ fontFamily: "Verizon-NHG-eTX, Helvetica, Arial, sans-serif", marginBottom: 0 }}>
          Vehicle Images (Optional) {localLoading && <span style={{ fontSize: "12px", color: "#666" }}>(Loading...)</span>}
        </h2>
        <button
          type="button"
          onClick={handleEnableToggle}
          disabled={localLoading}
          style={{
            padding: "6px 12px",
            cursor: localLoading ? "not-allowed" : "pointer",
            background: enableAsset ? "#E60000" : "#000",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            fontFamily: "Verizon-NHG-eTX, Helvetica, Arial, sans-serif",
            fontSize: 12,
            opacity: localLoading ? 0.6 : 1,
          }}
        >
          {localLoading ? "Loading..." : enableAsset ? "Disable Images" : "Enable Images"}
        </button>
      </div>
      {!enableAsset && (
        <div style={{ marginTop: 8, marginBottom: 12, fontSize: 12, color: "#6f7171" }}>
          Image feature disabled. Click &apos;Enable Images&apos; to upload or manage files.
        </div>
      )}
      {/* Error / status banner */}
      {fetchError && enableAsset && (
        <div style={{ background: "#fdecea", color: "#611a15", padding: "8px 12px", borderRadius: 4, margin: "12px 0", fontSize: 14 }}>
          {fetchError === "missing-identifiers" && "Missing vehicle or bundle identifier. Upload disabled."}
          {fetchError === "network" && "Images cannot be loaded due to network issues. Please check your connection and try again."}
          {fetchError === "http-error" && "Server error occurred while loading files. Please try again later."}
          {fetchError === "invalid-data" && "Invalid data format received from server. Please contact support."}
          {fetchError === "unexpected" && "Unexpected error occurred while loading files. Please try again."}
          {fetchError === "cancelled" && "File loading was cancelled."}
          <button
            onClick={() => setEnableAsset(false)}
            style={{
              marginLeft: 8,
              padding: "2px 6px",
              fontSize: 12,
              background: "transparent",
              border: "1px solid #611a15",
              color: "#611a15",
              borderRadius: 2,
              cursor: "pointer",
            }}
          >
            Dismiss
          </button>
        </div>
      )}
      <div className={styles.contentWrapper} style={!enableAsset ? { pointerEvents: "none", opacity: 0.5 } : {}}>
        {/* Left Side: File Upload Area (Drag and Drop Zone) */}
        <div
          className={`${styles.uploadArea} ${isUploadDisabled ? styles.disabledUpload : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          // Click handler for the whole dashed area to trigger the hidden file input
          onClick={() => !isUploadDisabled && fileInputRef.current?.click()}
        >
          {/* Hidden file input element */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }} // Hide the default browser file input
            accept="image/png, image/jpeg, application/pdf" // Specify accepted file types
            multiple // Allow selection of multiple files at once
            onChange={handleFileChange}
            disabled={isUploadDisabled}
          />
          <p className={styles.dragDropText}>Drag and drop file here</p>
          <p className={styles.orText}>or</p>
          <button
            className={styles.browseButton}
            style={{ fontFamily: "Verizon-NHG-eTX, Helvetica, Arial, sans-serif" }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent the parent div's click from also firing
              fileInputRef.current?.click();
            }}
            disabled={isUploadDisabled}
          >
            Browse Files
          </button>
          <p className={styles.infoText} style={{ fontFamily: "Verizon-NHG-eTX, Helvetica, Arial, sans-serif" }}>
            Supports PNG, JPG, PDF (Max {MAX_FILE_SIZE_MB}MB each)
          </p>
        </div>

        {/* Right Side: Current Files Display Area */}
        <div className={styles.currentImagesArea}>
          <p className={styles.currentImagesHeader} style={{ fontFamily: "Verizon-NHG-eTX, Helvetica, Arial, sans-serif" }}>
            Current Files ({currentImages.length}/{MAX_IMAGES} max)
          </p>
          <div className={styles.imageGrid}>
            {currentImages.map((fileData) => (
              // This outer div now acts as the container for both the image box and its name
              <div key={fileData.id} className={styles.fileContainer}>
                <div className={styles.imageItem} onClick={() => handlePreviewClick(fileData)}>
                  <div
                    className={styles.deleteIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage(fileData.id);
                    }}
                    role="button"
                    aria-label={`Delete file ${fileData.name}`}
                  >
                    &times;
                  </div>
                  {fileData.type.startsWith("image/") ? (
                    <img src={fileData.src} alt={fileData.name} className={styles.uploadedImage} />
                  ) : (
                    <img src={fileData.src} alt={`PDF: ${fileData.name}`} className={styles.pdfPlaceholderIcon} />
                  )}
                </div>
                <p className={styles.imageFilename}>{fileData.name}</p> {/* Filename outside the imageItem div */}
              </div>
            ))}

            {Array.from({ length: MAX_IMAGES - currentImages.length }).map((_, index) => (
              <div key={`placeholder-${index}`} className={styles.fileContainer}>
                {" "}
                {/* Also wrap placeholders */}
                <div className={styles.imageItemPlaceholder}></div>
                <p className={styles.imageFilename}></p> {/* Keep an empty p tag for consistent spacing */}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Render the Modal component */}
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        {modalContent}
      </Modal>
    </div>
  );
};

// Define PropTypes for component validation
VehicleImageUpload.propTypes = {
  // onImagesChange is a function that will be called whenever the list of files changes
  // It receives an array of File objects.
  onImagesChange: PropTypes.func.isRequired,
};

export default VehicleImageUpload;
